import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

// Custom vignette shader
const VignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    offset: { value: 1.0 },
    darkness: { value: 0.5 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float offset;
    uniform float darkness;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec2 uv = (vUv - vec2(0.5)) * vec2(offset);
      float vignette = 1.0 - dot(uv, uv);
      vignette = pow(vignette, darkness);
      texel.rgb *= vignette;
      gl_FragColor = texel;
    }
  `,
};

// Film grain shader
const FilmGrainShader = {
  uniforms: {
    tDiffuse: { value: null },
    time: { value: 0.0 },
    intensity: { value: 0.08 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float intensity;
    varying vec2 vUv;

    float random(vec2 co) {
      return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      float noise = random(vUv * time) * intensity;
      texel.rgb += noise;
      gl_FragColor = texel;
    }
  `,
};

// CRT color grading shader for bluish glow
const CRTColorShader = {
  uniforms: {
    tDiffuse: { value: null },
    colorTint: { value: new THREE.Vector3(0.95, 1.0, 1.15) }, // Slight blue tint
    glowIntensity: { value: 0.3 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform vec3 colorTint;
    uniform float glowIntensity;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);

      // Apply color tint for CRT effect
      texel.rgb *= colorTint;

      // Add subtle glow enhancement
      float luminance = dot(texel.rgb, vec3(0.299, 0.587, 0.114));
      texel.rgb += luminance * glowIntensity * colorTint;

      gl_FragColor = texel;
    }
  `,
};

export class PostProcessing {
  constructor(renderer, scene, camera) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    this.composer = new EffectComposer(renderer);

    // Add render pass
    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    // Add bloom for CRT glow effect
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.5,  // strength - strong glow
      0.6,  // radius - soft spread
      0.2   // threshold - lower to make more things glow
    );
    this.bloomPass = bloomPass;
    this.composer.addPass(bloomPass);

    // Add CRT color grading
    this.crtColorPass = new ShaderPass(CRTColorShader);
    this.crtColorPass.renderToScreen = false;
    this.composer.addPass(this.crtColorPass);

    // Add enhanced vignette effect for side-viewing
    this.vignettePass = new ShaderPass(VignetteShader);
    this.vignettePass.uniforms.darkness.value = 1.2; // Stronger vignette
    this.vignettePass.uniforms.offset.value = 1.3;   // Wider spread
    this.vignettePass.renderToScreen = false;
    this.composer.addPass(this.vignettePass);

    // Add film grain effect
    this.filmGrainPass = new ShaderPass(FilmGrainShader);
    this.filmGrainPass.uniforms.intensity.value = 0.12; // Slightly more grain for CRT feel
    this.filmGrainPass.renderToScreen = true;
    this.composer.addPass(this.filmGrainPass);
  }

  update(time) {
    // Update film grain time for animation
    this.filmGrainPass.uniforms.time.value = time;
  }

  render() {
    this.composer.render();
  }

  setSize(width, height) {
    this.composer.setSize(width, height);
  }

  dispose() {
    this.composer.dispose();
  }
}
